import { useTRPC } from '@/utils/trpc';
import { AoeQuestionnaireFields, getUnansweredRequiredLinkIds } from '@liza/ui/components/records';
import { Spinner } from '@liza/ui/components/ui/spinner';
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { SelectedTest, TestAoe } from './AddLabOrderForm';

export interface LabTestAoeFieldsProps {
  test: SelectedTest;
  /** This test's captured AOE state (fetched questionnaire + answers), or undefined before fetch. */
  value: TestAoe | undefined;
  /** Replace this test's AOE state (load flag, fetched questionnaire, captured answers). */
  onChange: (entry: TestAoe) => void;
  /** Flag unanswered required questions (true once the form has been submitted). */
  showErrors: boolean;
}

/**
 * Fetches a test's Ask-at-Order-Entry (AOE) questionnaire from Health Gorilla and renders its
 * inputs, flagging unanswered required questions once `showErrors` is set. Renders nothing for tests
 * with no AOEs. Reports a `loaded` flag into the captured state once the fetch succeeds so the form
 * can hold submit until every selected test's questions have downloaded — and surfaces an error
 * (leaving the test unloaded, so submit stays blocked) if the fetch fails, rather than silently
 * treating the test as AOE-free.
 */
export function LabTestAoeFields({ test, value, onChange, showErrors }: LabTestAoeFieldsProps): React.ReactNode {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const aoeQuery = useQuery(
    trpc.labOrder.getTestAoe.queryOptions({
      testCode: { system: test.system, code: test.code, display: test.display },
    }),
  );

  const fetched = aoeQuery.data?.questionnaire ?? undefined;

  // `onChange` is an inline arrow from the parent (new identity each render); hold it in a ref so the
  // effect only re-runs on actual fetch/answer changes, not on every parent re-render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Record the questionnaire (or its absence) and mark the test loaded once the fetch succeeds.
  useEffect(() => {
    if (!aoeQuery.isSuccess) return;
    const fetchedKey = fetched?.url ?? fetched?.id;
    const storedKey = value?.questionnaire?.url ?? value?.questionnaire?.id;
    if (!value?.loaded || fetchedKey !== storedKey) {
      onChangeRef.current({ loaded: true, questionnaire: fetched, responses: value?.responses });
    }
  }, [aoeQuery.isSuccess, fetched, value]);

  if (aoeQuery.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('labOrderAoeError')}
      </p>
    );
  }
  if (aoeQuery.isPending) {
    return <Spinner className="size-4" />;
  }

  const questionnaire = value?.questionnaire ?? fetched;
  if (!questionnaire || (questionnaire.item ?? []).length === 0) return null;

  const invalidLinkIds = showErrors
    ? new Set(getUnansweredRequiredLinkIds(questionnaire, value?.responses))
    : undefined;

  return (
    <AoeQuestionnaireFields
      questionnaire={questionnaire}
      response={value?.responses}
      onChange={(responses: QuestionnaireResponse) => onChange({ loaded: true, questionnaire, responses })}
      invalidLinkIds={invalidLinkIds}
    />
  );
}
